<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import KanbanHeader from './KanbanHeader.vue';
import KanbanAPI from '../../../../api/kanban';
import Modal from 'dashboard/components/Modal.vue';
import CustomContextMenu from 'dashboard/components/ui/CustomContextMenu.vue';
import SendMessageTemplate from './SendMessageTemplate.vue';
import KanbanItemForm from './KanbanItemForm.vue';

const { t, locale } = useI18n();
const store = useStore();
const router = useRouter();
const isLoading = ref(false);
const deals = ref([]);
const errorMessage = ref(null);
const searchQuery = ref('');
const activeFilters = ref(null);
const groupBy = ref('stage');

// Estado para itens do Kanban (similar ao KanbanColumn.vue)
const kanbanItems = ref([]);
const isLoadingItems = ref(false);
const currentPage = ref(1);
const hasMoreItems = ref(true);
const isLoadingMore = ref(false);

const emit = defineEmits([
  'switch-view',
  'itemClick',
  'force-update',
  'itemsUpdated',
]);

const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

// Removido: kanbanItems agora é um ref que buscamos diretamente da API
// const kanbanItems = computed(() => store.getters['kanban/getKanbanItems']);
// const isKanbanLoading = computed(() => store.state.kanban.loading);

const showDeleteModal = ref(false);
const showContextMenu = ref(false);
const showSendMessageModal = ref(false);
const contextMenuPosition = ref({ x: 0, y: 0 });
const itemToDelete = ref(null);
const selectedDeal = ref(null);
const isDeleting = ref(false);

const expandedStages = ref(new Set());
const showEditModal = ref(false);
const showAddModal = ref(false);
const itemToEdit = ref(null);
const selectedStageForAdd = ref(null);

const props = defineProps({
  currentView: {
    type: String,
    default: 'list',
  },
  // A propriedade 'kanbanItems' não é mais necessária, mas a mantive para evitar erros se ainda for passada
  kanbanItems: {
    type: Array,
    required: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  labelsMap: {
    type: Object,
    default: () => ({}),
  },
});

const funnelsCache = computed(() => {
  const allFunnels = store.getters['funnel/getFunnels'] || [];
  const cache = {};
  allFunnels.forEach(funnel => {
    cache[funnel.id] = funnel;
  });
  return cache;
});

const processDeals = () => {
  try {
    const currentFunnel = selectedFunnel.value;
    if (!currentFunnel?.id) {
      errorMessage.value = 'Nenhum funil selecionado';
      deals.value = [];
      return;
    }

    const itemsToProcess = kanbanItems.value;
    if (!Array.isArray(itemsToProcess)) {
      console.error('kanbanItems não é um array. Não é possível processar.');
      deals.value = [];
      return;
    }

    const currentFunnelItems = itemsToProcess.filter(
      item => String(item.funnel_id) === String(currentFunnel.id)
    );

    console.log(
      'Items encontrados para o funil atual:',
      currentFunnelItems.length
    );

    deals.value = currentFunnelItems.map(item => {
      const stageName = currentFunnel.stages[item.funnel_stage]?.name || '';
      const stageColor =
        currentFunnel.stages[item.funnel_stage]?.color || '#64748B';

      return {
        id: item.id,
        title: item.item_details?.title || '',
        description: item.item_details?.description || '',
        priority: item.item_details?.priority || 'none',
        assignee: item.item_details?.agent || null,
        value: item.item_details?.value || 0,
        stage: item.funnel_stage,
        stageName,
        stageColor,
        createdAt: new Date(item.created_at).toLocaleDateString(),
        item_details: item.item_details,
        funnel_id: String(item.funnel_id),
        // Checklist completo do item, usado para sugerir próxima ação
        checklist: item.checklist,
        // Dados da conversa já retornados pela API de kanban items
        conversation: item.conversation || null,
        conversation_display_id:
          item.conversation_display_id || item.conversation?.display_id || null,
      };
    });

    console.log('Deals processados:', deals.value.length);
  } catch (err) {
    console.error('Erro ao processar deals:', err);
    errorMessage.value = err.message;
  }
};

// Próxima ação sugerida com base no checklist de cada deal
const getNextChecklistItem = deal => {
  const checklist = deal?.checklist;
  if (!Array.isArray(checklist)) return null;
  return checklist.find(item => !item.completed) || null;
};

// Helpers para exibir informações da conversa vinculada
const getConversation = deal => deal?.conversation || null;

const getConversationSenderName = deal => {
  const conversation = getConversation(deal);
  if (!conversation) return '';
  const name = conversation.contact?.name;
  const email = conversation.contact?.email;
  const defaultText = t('ITEM_CONVERSATION.NO_CONTACT');
  const text = name || email || defaultText;
  return text.length > 25 ? `${text.substring(0, 25)}...` : text;
};

const getConversationLastMessage = deal => {
  const conversation = getConversation(deal);
  const message = conversation?.last_message?.content;
  if (!message) return '';
  const cleanMessage = message.replace(/\s+/g, ' ').trim();
  return cleanMessage.length > 40
    ? `${cleanMessage.substring(0, 40)}...`
    : cleanMessage;
};

// Função para buscar itens do Kanban (similar ao KanbanColumn.vue)
const fetchKanbanItems = async (page = 1, reset = false) => {
  if (isLoadingItems.value) return;

  try {
    isLoadingItems.value = true;
    if (page > 1) {
      isLoadingMore.value = true;
    }

    console.log(`[ListTab] Buscando itens do funil, página ${page}`);

    // Buscar todos os itens do funil atual
    const response = await KanbanAPI.getItems(selectedFunnel.value?.id);
    const items = response.data.items || response.data;

    console.log(`[ListTab] API retornou ${items.length} itens`);

    if (reset || page === 1) {
      kanbanItems.value = items;
      currentPage.value = 1;
    } else {
      kanbanItems.value = [...kanbanItems.value, ...items];
    }

    currentPage.value = page;
    hasMoreItems.value = items.length === 50; // Assumindo que 50 é o limite padrão

    console.log(`[ListTab] Agora tem ${kanbanItems.value.length} itens`);
  } catch (error) {
    console.error('[ListTab] Erro ao buscar itens:', error);
  } finally {
    isLoadingItems.value = false;
    isLoadingMore.value = false;
  }
};

const fetchData = async () => {
  isLoading.value = true;
  if (selectedFunnel.value) {
    await fetchKanbanItems(1, true);
  }
  isLoading.value = false;
};

watch(
  () => kanbanItems.value,
  () => processDeals()
);

watch(
  () => selectedFunnel.value,
  () => processDeals()
);

watch(
  () => store.getters['funnel/getFunnels'],
  () => processDeals()
);

// Watcher para mudanças no funil selecionado (similar ao KanbanColumn.vue)
watch(
  selectedFunnel,
  async (newFunnel, oldFunnel) => {
    if (newFunnel && newFunnel.id !== oldFunnel?.id) {
      console.log(`[ListTab] Funil mudou para ${newFunnel.id}, recarregando itens`);
      await fetchKanbanItems(1, true);
    }
  },
  { immediate: false } // Não executar imediatamente, deixar o onMounted fazer isso
);

onMounted(() => {
  if (selectedFunnel.value) {
    fetchData();
  }
});

const filteredDeals = computed(() => {
  let filtered = deals.value;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      deal =>
        deal.title.toLowerCase().includes(query) ||
        deal.description.toLowerCase().includes(query) ||
        deal.assignee?.name.toLowerCase().includes(query)
    );
  }

  if (activeFilters.value) {
    const filters = activeFilters.value;

    filtered = filtered.filter(deal => {
      let matches = true;
      if (filters.priority?.length) {
        matches = matches && filters.priority.includes(deal.priority);
      }
      if (filters.value?.min || filters.value?.max) {
        const dealValue = parseFloat(deal.value) || 0;
        if (filters.value.min) {
          matches = matches && dealValue >= parseFloat(filters.value.min);
        }
        if (filters.value.max) {
          matches = matches && dealValue <= parseFloat(filters.value.max);
        }
      }
      if (filters.agent_id) {
        matches = matches && deal.assignee?.id === filters.agent_id;
      }
      if (filters.date?.start || filters.date?.end) {
        const dealDate = new Date(deal.createdAt);
        if (filters.date.start) {
          matches = matches && dealDate >= new Date(filters.date.start);
        }
        if (filters.date.end) {
          matches = matches && dealDate <= new Date(filters.date.end);
        }
      }
      return matches;
    });
  }

  return filtered;
});

const handleSearch = query => {
  searchQuery.value = query;
};

const handleFilter = filters => {
  activeFilters.value = filters;
};

const handleGroupByChange = value => {
  groupBy.value = value;
};

const searchResults = computed(() => {
  if (!searchQuery.value) return { total: 0, stages: {} };

  const results = {
    total: filteredDeals.value.length,
    stages: {},
  };

  filteredDeals.value.forEach(deal => {
    if (!results.stages[deal.stageName]) {
      results.stages[deal.stageName] = 0;
    }
    results.stages[deal.stageName]++;
  });

  return results;
});

const groupedDeals = computed(() => {
  const currentFunnel = selectedFunnel.value;
  if (groupBy.value === 'stage') {
    if (!currentFunnel?.stages) {
      return [];
    }
    const groups = {};
    Object.entries(currentFunnel.stages).forEach(([stageId, stageInfo]) => {
      groups[stageId] = {
        id: stageId,
        name: stageInfo.name || '',
        color: stageInfo.color || '#64748B',
        deals: [],
        totalValue: 0,
        position: stageInfo.position || 0,
      };
    });
    filteredDeals.value.forEach(deal => {
      if (groups[deal.stage]) {
        groups[deal.stage].deals.push(deal);
        groups[deal.stage].totalValue += Number(deal.value) || 0;
      }
    });
    return Object.values(groups).sort((a, b) => a.position - b.position);
  }

  const groups = {};
  filteredDeals.value.forEach(deal => {
    const isAgentGrouping = groupBy.value === 'agent';
    const groupKey = isAgentGrouping
      ? String(deal.assignee?.id || 'unassigned')
      : String(
          deal.conversation?.inbox?.id ||
            deal.conversation?.inbox_id ||
            deal.conversation?.meta?.channel ||
            'unknown_channel'
        );
    const groupName = isAgentGrouping
      ? deal.assignee?.name || t('KANBAN.LIST_GROUP_BY.UNASSIGNED_AGENT')
      : deal.conversation?.inbox?.name ||
        deal.conversation?.meta?.channel ||
        t('KANBAN.LIST_GROUP_BY.UNKNOWN_CHANNEL');

    if (!groups[groupKey]) {
      groups[groupKey] = {
        id: groupKey,
        name: groupName,
        color: '#64748B',
        deals: [],
        totalValue: 0,
        position: Number.MAX_SAFE_INTEGER,
      };
    }

    groups[groupKey].deals.push(deal);
    groups[groupKey].totalValue += Number(deal.value) || 0;
  });

  return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
});

const getFriendlyStageLabel = stageKey => {
  if (stageKey.startsWith('etapa_')) {
    const num = stageKey.replace('etapa_', '');
    return `Etapa ${num}`;
  }
  return stageKey.charAt(0).toUpperCase() + stageKey.slice(1);
};

const handleEdit = deal => {
  itemToEdit.value = {
    id: deal.id,
    title: deal.title,
    description: deal.description,
    funnel_id: selectedFunnel.value?.id,
    funnel_stage: deal.stage,
    item_details: {
      title: deal.title,
      description: deal.description,
      priority: deal.priority,
      value: deal.value,
      agent_id: deal.assignee?.id,
    },
    custom_attributes: deal.custom_attributes || {},
  };
  showEditModal.value = true;
};

const handleItemEdited = () => {
  showEditModal.value = false;
  itemToEdit.value = null;
  emit('force-update');
};

const handleAdd = stageId => {
  if (!selectedFunnel.value?.id) return;
  selectedStageForAdd.value = stageId;
  showAddModal.value = true;
};

const handleDelete = deal => {
  itemToDelete.value = deal;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  if (!itemToDelete.value) return;
  try {
    isDeleting.value = true;
    await KanbanAPI.deleteItem(itemToDelete.value.id);
    emit('force-update');
    showDeleteModal.value = false;
    itemToDelete.value = null;
  } catch (error) {
    console.error('Erro ao excluir item:', error);
  } finally {
    isDeleting.value = false;
  }
};

const handleContextMenu = (e, deal) => {
  e.preventDefault();
  selectedDeal.value = deal;
  showContextMenu.value = true;
  contextMenuPosition.value = {
    x: e.clientX,
    y: e.clientY,
  };
};

const handleQuickMessage = () => {
  showContextMenu.value = false;
  showSendMessageModal.value = true;
};

const handleSendMessage = () => {
  showSendMessageModal.value = false;
  showContextMenu.value = false;
};

const handleItemClick = deal => {
  emit('itemClick', deal);
};

const navigateToConversation = (e, conversationDisplayId, deal) => {
  e.stopPropagation();
  const conversation = deal?.conversation;
  if (!conversationDisplayId || !conversation) return;

  try {
    const route = router.resolve({
      name: 'inbox_conversation',
      params: {
        accountId: conversation.account_id,
        inboxId: conversation.inbox_id,
        conversation_id: conversationDisplayId,
      },
    });
    window.open(route.href, '_blank');
  } catch (error) {
    // Fallback simples para navegação direta
    window.location.href = `/app/accounts/${conversation.account_id}/conversations/${conversationDisplayId}`;
  }
};

const toggleStage = stageId => {
  if (expandedStages.value.has(stageId)) {
    expandedStages.value.delete(stageId);
  } else {
    expandedStages.value.add(stageId);
  }
};

const isStageExpanded = stageId => {
  const stage = groupedDeals.value.find(s => s.id === stageId);
  if (!stage) return false;

  // Estado inicial: se ainda não há estágios no set,
  // inicia expandido para grupos que tiverem itens.
  if (!expandedStages.value.size) {
    return stage.deals.length > 0;
  }

  return expandedStages.value.has(stageId);
};

const refreshData = () => {
  emit('force-update');
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-white dark:bg-slate-900">
    <KanbanHeader
      :current-view="currentView"
      :search-results="searchResults"
      :active-filters="activeFilters"
      :group-by="groupBy"
      @filter="handleFilter"
      @search="handleSearch"
      @group-by-change="handleGroupByChange"
      @switch-view="view => emit('switch-view', view)"
    />

    <div class="deals-list p-4 flex-1 overflow-auto w-full">
      <div
        v-if="isKanbanLoading || isLoading"
        class="flex justify-center items-center py-12"
      >
        <span class="loading-spinner" />
      </div>

      <div
        v-else-if="errorMessage"
        class="flex justify-center items-center py-12 text-ruby-500"
      >
        {{ errorMessage }}
      </div>

      <div
        v-else-if="!filteredDeals.length"
        class="flex flex-col items-center justify-center py-12"
      >
        <fluent-icon icon="task" size="48" class="mb-4 text-slate-400" />
        <p class="text-lg text-slate-600">{{ t('KANBAN.NO_ITEMS') }}</p>
      </div>

      <div v-else class="space-y-2 w-full max-w-full">
        <div
          v-for="stage in groupedDeals"
          :key="stage.id"
          class="deal-group w-full bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700"
        >
          <div
            class="stage-header p-3 flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-t-lg cursor-pointer"
            @click="toggleStage(stage.id)"
          >
            <div class="flex items-center gap-2">
              <span
                class="w-3 h-3 rounded-full"
                :style="{ backgroundColor: stage.color }"
              />
              <h3 class="font-medium">
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{ t('KANBAN.STAGE.PREFIX') }}
                </span>
                <span class="ml-1 text-slate-800 dark:text-slate-200">
                  {{ stage.name }}
                </span>
              </h3>
              <span
                class="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-200 dark:bg-slate-700"
              >
                {{ stage.deals.length }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <!-- Botão Novo item com estilo azul, alinhado ao header -->
              <button
                type="button"
                class="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors"
                :style="{
                  backgroundColor: '#2563EB',
                  color: '#FFFFFF',
                  border: '1px solid #1D4ED8'
                }"
                @click.stop="handleAdd(stage.id)"
              >
                <fluent-icon icon="add" size="14" class="mr-1" />
                {{ t('KANBAN.NEW_ITEM') }}
              </button>

              <div
                class="text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{
                  new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: t('KANBAN.CURRENCY'),
                  }).format(stage.totalValue)
                }}
              </div>
              <fluent-icon
                :icon="
                  isStageExpanded(stage.id) ? 'chevron-up' : 'chevron-down'
                "
                size="16"
                class="text-slate-500"
              />
            </div>
          </div>

          <div
            v-show="isStageExpanded(stage.id)"
            class="divide-y divide-slate-100 dark:divide-slate-800"
          >
            <div
              v-for="deal in stage.deals"
              :key="deal.id"
              class="p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer flex items-center gap-3"
              @click="handleItemClick(deal)"
            >
              <div class="flex-1">
                <div class="flex items-center justify-between gap-2">
                  <div class="font-medium text-slate-900 dark:text-slate-100">
                    {{ deal.title }}
                  </div>
                  <div
                    class="text-right text-sm font-medium text-slate-800 dark:text-slate-200 whitespace-nowrap"
                  >
                    {{
                      new Intl.NumberFormat(locale, {
                        style: 'currency',
                        currency: t('KANBAN.CURRENCY'),
                      }).format(deal.value)
                    }}
                  </div>
                </div>
                <div
                  v-if="deal.description"
                  class="text-sm text-slate-500 dark:text-slate-400 truncate max-w-md"
                >
                  {{ deal.description }}
                </div>

                <!-- Linha de widgets: próxima ação + conversa vinculada lado a lado -->
                <div
                  v-if="getNextChecklistItem(deal) || deal.conversation"
                  class="mt-1 flex flex-col gap-1 md:flex-row md:items-stretch md:gap-2"
                >
                  <!-- Sugestão de Próxima Ação (igual ao KanbanItem.vue) -->
                  <div
                    v-if="getNextChecklistItem(deal)"
                    class="px-2 py-1.5 rounded-md border flex items-start gap-2 md:flex-1"
                    :style="{
                      backgroundColor: '#FEF9C3',
                      borderColor: '#FBBF24'
                    }"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-list-todo-icon flex-shrink-0 mt-0.5"
                      :style="{ color: '#FBBF24' }"
                    >
                      <path d="M13 5h8" />
                      <path d="M13 12h8" />
                      <path d="M13 19h8" />
                      <path d="m3 17 2 2 4-4" />
                      <rect x="3" y="4" width="6" height="6" rx="1" />
                    </svg>
                    <div class="flex flex-col min-w-0">
                      <span
                        class="text-[11px] font-medium text-slate-500 uppercase tracking-wide"
                      >
                        {{
                          t(
                            'KANBAN.FORM.CHECKLIST.NEXT_ACTION_TITLE'
                          )
                        }}
                      </span>
                      <span
                        class="text-xs text-slate-700 dark:text-slate-200 truncate"
                        :title="getNextChecklistItem(deal)?.text"
                      >
                        {{ getNextChecklistItem(deal)?.text }}
                      </span>
                    </div>
                  </div>

                  <!-- Informações da conversa vinculada (usando dados da API de kanban items) -->
                  <div
                    v-if="deal.conversation"
                    class="px-2 py-1.5 rounded-md border flex items-center gap-2 md:flex-1 text-xs"
                    :style="{
                      backgroundColor: '#F8FAFC',
                      borderColor: '#E2E8F0'
                    }"
                    @click.stop="
                      navigateToConversation(
                        $event,
                        deal.conversation_display_id,
                        deal
                      )
                    "
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="text-slate-400 flex-shrink-0"
                    >
                      <path
                        d="M3 20l1.3-3.9A8 8 0 1 1 11.9 20L8 21.3 3 20z"
                      />
                    </svg>
                    <div class="flex flex-col min-w-0 flex-1">
                      <div class="flex items-center gap-1 text-[11px]">
                        <span
                          class="px-1.5 py-0.5 rounded-full border"
                          :style="{
                            borderColor: '#CBD5E1',
                            color: '#0F172A'
                          }"
                        >
                          #{{ deal.conversation.display_id }}
                        </span>
                        <span class="truncate text-slate-600 dark:text-slate-200">
                          {{ getConversationSenderName(deal) }}
                        </span>
                      </div>
                      <span
                        v-if="getConversationLastMessage(deal)"
                        class="truncate text-[11px] text-slate-500 dark:text-slate-400"
                        :title="deal.conversation.last_message?.content"
                      >
                        "{{ getConversationLastMessage(deal) }}"
                      </span>
                    </div>
                    <span
                      class="ml-2 text-[11px] px-1.5 py-0.5 rounded-full border flex-shrink-0"
                      :style="{
                        borderColor: '#CBD5E1',
                        color: '#0F172A'
                      }"
                    >
                      {{ deal.conversation.status }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-if="deal.assignee" class="flex items-center gap-2">
                <span
                  v-if="!deal.assignee.avatar"
                  class="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600"
                >
                  {{ deal.assignee.name.charAt(0) }}
                </span>
                <img
                  v-else
                  :src="deal.assignee.avatar"
                  :alt="deal.assignee.name"
                  class="w-8 h-8 rounded-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Modal
    v-model:show="showAddModal"
    size="full-width"
    :on-close="
      () => {
        showAddModal = false;
        selectedStageForAdd = null;
      }
    "
  >
    <div class="p-6">
      <h3 class="text-lg font-medium">
        {{ t('KANBAN.ADD_ITEM') }}
      </h3>
      <KanbanItemForm
        v-if="selectedFunnel && selectedStageForAdd"
        :funnel-id="selectedFunnel.id"
        :stage="selectedStageForAdd"
        @saved="
          () => {
            showAddModal = false;
            selectedStageForAdd = null;
            refreshData();
          }
        "
        @close="
          () => {
            showAddModal = false;
            selectedStageForAdd = null;
          }
        "
      />
    </div>
  </Modal>

  <Modal
    v-model:show="showDeleteModal"
    :on-close="
      () => {
        showDeleteModal = false;
        itemToDelete = null;
      }
    "
  >
    <div class="p-6">
      <h3 class="text-lg font-medium mb-4">
        {{ t('KANBAN.DELETE.TITLE') }}
      </h3>
      <p class="text-sm text-slate-600 mb-6">
        {{ t('KANBAN.DELETE.DESCRIPTION', { item: itemToDelete?.title }) }}
      </p>
      <div class="flex justify-end gap-2">
        <woot-button
          variant="clear"
          size="small"
          @click="showDeleteModal = false"
        >
          {{ t('KANBAN.CANCEL') }}
        </woot-button>
        <woot-button
          variant="danger"
          size="small"
          :loading="isDeleting"
          @click="confirmDelete"
        >
          {{ t('KANBAN.DELETE') }}
        </woot-button>
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.deals-list {
  min-height: 0;
  width: 100%;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #4f46e5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.deal-group {
  animation: fadeIn 0.2s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.deal-group:nth-child(1) {
  animation-delay: 0s;
}
.deal-group:nth-child(2) {
  animation-delay: 0.05s;
}
.deal-group:nth-child(3) {
  animation-delay: 0.1s;
}
.deal-group:nth-child(4) {
  animation-delay: 0.15s;
}
.deal-group:nth-child(5) {
  animation-delay: 0.2s;
}
</style>